import { ApiPromise } from '@polkadot/api';
import { InjectedExtension } from '@polkadot/extension-inject/types';
import { KeyringPair as PolkadotKeyringPair, KeyringPair$Meta } from '@polkadot/keyring/types';
import type { DefinitionRpcExt } from '@polkadot/types/types';
import { Keyring } from '@polkadot/ui-keyring/Keyring';

enum KeyringStatus {
  Idle = 'IDLE',
  Loading = 'LOADING',
  Ready = 'READY',
  Error = 'ERROR'
}

enum ApiStatus {
  Idle = 'IDLE',
  ConnectInit = 'CONNECT_INIT',
  Connecting = 'CONNECTING',
  Ready = 'READY',
  Error = 'ERROR',
  Disconnected = 'DISCONNECTED'
}

enum ActionType {
  ConnectInit = 'CONNECT_INIT',
  Connect = 'CONNECT',
  ConnectSuccess = 'CONNECT_SUCCESS',
  ConnectError = 'CONNECT_ERROR',
  ConnectFail = 'CONNECT_FAIL',
  SetKeyringLoading = 'SET_KEYRING_LOADING',
  SetKeyringReady = 'SET_KEYRING_READY',
  SetKeyringError = 'SET_KEYRING_ERROR',
  SetSelectedAccount = 'SET_SELECTED_ACCOUNT',
  SetAccounts = 'SET_ACCOUNTS',
  SetExtensions = 'SET_EXTENSIONS'
}

interface APIError extends Error {
  target: {
    url: string;
  };
}

interface KeyringPairMeta extends KeyringPair$Meta {
  name: string;
  source: string;
}

interface KeyringPair extends PolkadotKeyringPair {
  meta: KeyringPairMeta;
}

type Action =
  | { type: ActionType.ConnectInit }
  | { type: ActionType.Connect; payload: ApiPromise }
  | { type: ActionType.ConnectSuccess }
  | { type: ActionType.ConnectError; payload: APIError }
  | { type: ActionType.ConnectFail }
  | { type: ActionType.SetKeyringLoading }
  | { type: ActionType.SetKeyringReady; payload: Keyring }
  | { type: ActionType.SetKeyringError }
  | { type: ActionType.SetSelectedAccount; payload: KeyringPair | undefined }
  | { type: ActionType.SetAccounts; payload: Array<KeyringPair> }
  | { type: ActionType.SetExtensions; payload: Array<InjectedExtension> };

type Dispatch = (action: Action) => void;

type State = {
  socket: string | undefined;
  jsonrpc: Record<string, Record<string, DefinitionRpcExt>>;
  keyring: Keyring | undefined;
  keyringStatus: KeyringStatus;
  api: ApiPromise | undefined;
  apiError: APIError | undefined;
  apiStatus: ApiStatus;
  selectedAccount: KeyringPair | undefined;
  accounts: Array<KeyringPair>;
  extensions: Array<InjectedExtension>;
};

type SecureState = Omit<State, 'keyring' | 'api'> & {
  keyring: Keyring;
  api: ApiPromise;
};

type SubstrateProviderProps = {
  children: React.ReactNode;
  socket?: string;
};

interface SubstrateStateContextInterface {
  state: State;
  setSelectedAccount: (newAccount: PolkadotKeyringPair) => Promise<void>;
  removeSelectedAccount: () => void;
}

export type {
  Action,
  Dispatch,
  KeyringPair,
  PolkadotKeyringPair,
  SecureState,
  State,
  SubstrateProviderProps,
  SubstrateStateContextInterface
};

export { ActionType, ApiStatus, KeyringStatus };
